import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import {
    createHandlerChainRunner,
    HandlerWithResult,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import { Session } from "@insite/client-framework/Services/SessionService";
import {
    getSettingsCollection,
    getShouldLoadInventory,
    getShouldLoadPricing,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import flatMap from "lodash/flatMap";

type HandlerType = HandlerWithResult<
    {
        product: ProductModel;
        productInfo?: ProductInfo;
        session: Session;
        brandId?: string | null;
        categoryIdWithBrandId?: string | null;
        categoryId?: string | null;
    },
    { structuredPageData: string }
>;

export const PrepareStructuredPageData: HandlerType = async props => {
    const {
        id,
        productTitle,
        smallImagePath,
        mediumImagePath,
        largeImagePath,
        canonicalUrl,
        manufacturerItem,
        detail,
        properties,
        attributeTypes,
        brand: productBrand,
    } = props.parameter.product;
    const state = props.getState();

    const dataViews = JSON.parse(JSON.stringify(state.data.products.dataViews));
    const byIds = JSON.parse(JSON.stringify(state.data.products.byId));
    const currencyCode = JSON.parse(JSON.stringify(state.context?.session?.currency?.currencyCode));
    const productInfoLists = JSON.parse(JSON.stringify(state.components.productInfoLists));
    const sizeList = [
        "Nominal Size",
        "Size",
        "Connection Size",
        "Inlet Size",
        "Outlet Size",
        "Tube Size",
        "Orifice Size",
        "Pipe Size",
        "Port Size",
    ];
    const materialList = ["Material", "Body Material"];
    const colorList = ["Color", "Finish", "Finish/Color", "Finish/Coating"];
    const model = ['Series'];

    interface RelationshipData {
        [key: string]: any[];
    }

    const relationshipData: RelationshipData = {
        isAccessoryOrSparePartFor: [],
        isSimilarTo: [],
        isRelatedTo: [],
    };

    // Get the current browser domain
    const currentDomain = `${window.location.protocol}//${window.location.host}`;
    function isNullOrUndefinedOrEmpty(str: String) {
        return str == null || str === "" || str === "null" || str === "undefined";
    }
    function prependDomainIfUserfile(inputString: String) {
        // Check if the string starts with "/userfiles"
        if (inputString.startsWith("/userfiles")) {
            // Prepend the current domain
            return `${isNullOrUndefinedOrEmpty(inputString) ? currentDomain : currentDomain + inputString}`;
        }

        // Return the original string if condition is not met
        return inputString;
    }

    const generageDataStructure = (product: any) => {
        const payload: any = {
            "@type": "Product",
            name: product?.productTitle,
            sku: product?.detail.sku,
            url: isNullOrUndefinedOrEmpty(product?.canonicalUrl)
                ? currentDomain
                : currentDomain + product?.canonicalUrl,
            image: product?.images?.flatMap((image: any) => [
                prependDomainIfUserfile(image?.smallImagePath),
                prependDomainIfUserfile(image?.mediumImagePath),
                prependDomainIfUserfile(image?.largeImagePath),
            ]),
            description: product?.properties?.longDescription1
                ? product?.properties?.longDescription1
                : product?.properties?.longDescription2,
        };
        if (product?.pricingAndInventoryData) {
            const { pricing, inventory, unitOfMeasure } = product?.pricingAndInventoryData;
            if (pricing && inventory && pricing.unitNetPrice > 0) {
                const availability = inventory?.inventoryAvailabilityDtos?.find(
                    (o: any) => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
                )?.availability;

                payload.offers = {
                    "@type": "Offer",
                    availability:
                        availability?.messageType !== 2
                            ? availability?.messageType === 3
                                ? "https://schema.org/LimitedAvailability"
                                : "https://schema.org/InStock"
                            : "https://schema.org/OutOfStock",
                    price: pricing?.unitNetPrice,
                    priceCurrency: currencyCode ? currencyCode || "" : "",
                };
            }
        }

        if (product?.brand) {
            payload.brand = {
                "@type": "Brand",
                identifier: product?.brand?.id,
                name: product?.brand?.name,
                url: isNullOrUndefinedOrEmpty(product?.brand?.detailPagePath)
                    ? currentDomain
                    : currentDomain + product?.brand?.detailPagePath,
            };
        }
        return payload;
    };
    const getPricingAndInventoryData = (data: any, id: any) => {
        const productInfoLists = data?.productInfoListById;
        if (!productInfoLists) {
            return null;
        }

        // Iterate over productInfoListById keys
        for (const key in productInfoLists) {
            const productInfoByProductId = productInfoLists[key]?.productInfoByProductId;
            if (productInfoByProductId && productInfoByProductId[id]?.pricing) {
                return { pricing: productInfoByProductId[id].pricing, inventory: productInfoByProductId[id].inventory };
            }
        }
        return null;
    };
    const convertData = () => {
        for (const key in dataViews) {
            if (dataViews.hasOwnProperty(key)) {
                // Extract the relationship value from the key
                const match = key.match(/relationship=([^&]+)/);
                if (match) {
                    const relationship = match[1];
                    const ids = dataViews[key].ids || [];

                    // if (relationship === "Accessory" || relationship === "Spare Parts") {
                    // // Merge "Accessory" and "Spare Parts" into "isAccessoryOrSparePartFor"
                    // if(byIds){
                    //     ids.forEach((id:any) => {
                    //         const pricingAndInventoryData = getPricingAndInventoryData(productInfoLists,id);
                    //         const product = {...byIds[id], pricingAndInventoryData};
                    //         if (product) {
                    //         relationshipData?.isAccessoryOrSparePartFor?.push(generageDataStructure(product));
                    //         }
                    //     });
                    // }
                    // } else
                    if (relationship === "Similar Items") {
                        // Put "Similar Items" into "isSimilarTo"
                        if (byIds) {
                            ids.forEach((id: any) => {
                                const pricingAndInventoryData = getPricingAndInventoryData(productInfoLists, id);
                                const product = { ...byIds[id], pricingAndInventoryData };
                                if (product) {
                                    relationshipData?.isSimilarTo?.push(generageDataStructure(product));
                                }
                            });
                        }
                    } else if (relationship === "Related Items") {
                        // Put "Related Items" into "isRelatedTo"
                        if (byIds) {
                            ids.forEach((id: any) => {
                                const pricingAndInventoryData = getPricingAndInventoryData(productInfoLists, id);
                                const product = { ...byIds[id], pricingAndInventoryData };
                                if (product) {
                                    relationshipData?.isRelatedTo?.push(generageDataStructure(product));
                                }
                            });
                        }
                    } else {
                        if (!relationshipData[relationship]) {
                            relationshipData[relationship] = [];
                        }
                        if (byIds) {
                            ids.forEach((id: any) => {
                                const pricingAndInventoryData = getPricingAndInventoryData(productInfoLists, id);
                                const product = { ...byIds[id], pricingAndInventoryData };
                                if (product) {
                                    relationshipData[relationship].push(generageDataStructure(product));
                                }
                            });
                        }
                    }
                }
            }
        }
    };
    const getSizeData = () => {
        const values: string[] = [];

        attributeTypes?.forEach(obj => {
            if (sizeList.includes(obj.name)) {
                obj.attributeValues?.forEach(attr => {
                    values.push(attr.value);
                });
            }
        });

        return values.join(", ");
    };
    const getMaterialData = () => {
        const values: string[] = [];

        attributeTypes?.forEach(obj => {
            if (materialList.includes(obj.name)) {
                obj.attributeValues?.forEach(attr => {
                    values.push(attr.value);
                });
            }
        });

        return values.join(", ");
    };
    const getColorData = () => {
        const values: string[] = [];

        attributeTypes?.forEach(obj => {
            if (colorList.includes(obj.name)) {
                obj.attributeValues?.forEach(attr => {
                    values.push(attr.value);
                });
            }
        });

        return values.join(", ");
    };
    const getModelData = () => {
        const values: string[] = [];

        attributeTypes?.forEach(obj => {
            if (model.includes(obj.name)) {
                obj.attributeValues?.forEach(attr => {
                    values.push(attr.value);
                });
            }
        });

        return values.join(", ");
    };
    convertData();
    const sizeData = getSizeData();
    const materialData = getMaterialData();
    const colorData = getColorData();
    const modelData = getModelData();

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        productID: id,
        name: productTitle,
        image: [
            prependDomainIfUserfile(smallImagePath),
            prependDomainIfUserfile(mediumImagePath),
            prependDomainIfUserfile(largeImagePath),
        ],
        mpn: manufacturerItem,
        sku: detail?.sku,
        url: isNullOrUndefinedOrEmpty(canonicalUrl) ? currentDomain : currentDomain + canonicalUrl,
        weight: detail?.shippingWeight,
        category: properties?.categoryTitle,
        material: materialData,
        size: sizeData,
        color: colorData,
        model : modelData,
        description : properties?.longDescription1,
        countryOfOrigin: "US",
        gtin: detail?.upcCode,
        isSimilarTo: relationshipData.isSimilarTo,
        isRelatedTo: relationshipData.isRelatedTo,
    } as any;

    const productInfo = props.parameter.productInfo;
    if (
        productInfo &&
        !props.parameter.product.quoteRequired &&
        getShouldLoadPricing(state) &&
        getShouldLoadInventory(state)
    ) {
        await waitFor(() => !!productInfo.pricing && !!productInfo.inventory);

        const { pricing, inventory, unitOfMeasure } = productInfo;

        if (pricing && inventory && pricing.unitNetPrice > 0) {
            const availability = inventory?.inventoryAvailabilityDtos?.find(
                o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
            )?.availability;

            data.offers = {
                "@type": "Offer",
                availability:
                    availability?.messageType !== 2
                        ? availability?.messageType === 3
                            ? "https://schema.org/LimitedAvailability"
                            : "https://schema.org/InStock"
                        : "https://schema.org/OutOfStock",
                price: pricing.unitNetPrice,
                priceCurrency: props.parameter.session?.currency?.currencyCode,
                url: isNullOrUndefinedOrEmpty(canonicalUrl) ? currentDomain : currentDomain + canonicalUrl,
            };
        }
    }

    if (!data.offers) {
        props.result = { structuredPageData: "" };
        return;
    }

    if (props.parameter.brandId) {
        let brandState = getBrandStateById(state, props.parameter.brandId);
        let brand = productBrand || brandState.value;
        if (!brand && !brandState.isLoading) {
            const awaitableLoadBrand = makeHandlerChainAwaitable(loadBrand);
            await awaitableLoadBrand({ brandId: props.parameter.brandId })(props.dispatch, props.getState);
            brandState = getBrandStateById(props.getState(), props.parameter.brandId);
            brand = brandState.value;
        }

        if (brand) {
            data.brand = {
                "@type": "Brand",
                identifier: brand.id,
                name: brand.name,
                url: isNullOrUndefinedOrEmpty(brand.detailPagePath)
                    ? currentDomain
                    : currentDomain + brand.detailPagePath,
            };
        }
    }

    props.result = { structuredPageData: JSON.stringify(data) };
};

export const DispatchSetStructuredPageData: HandlerType = props => {
    props.dispatch({
        type: "Pages/ProductDetails/SetStructuredPageData",
        structuredPageData: props.result.structuredPageData,
    });
};

export const DispatchSetStructuredPageDataSetForId: HandlerType = props => {
    props.dispatch({
        type: "Pages/ProductDetails/SetStructuredPageDataSetForId",
        productId: props.parameter.product.id,
    });
};

export const chain = [PrepareStructuredPageData, DispatchSetStructuredPageData, DispatchSetStructuredPageDataSetForId];

const generateStructuredPageData = createHandlerChainRunner(chain, "GenerateStructuredPageData");
export default generateStructuredPageData;
